import { addDays, parseIso, todayIso, type BudgetPeriod, type Dashboard, type Transaction } from '@fin/shared';
import { useEffect, useMemo, useRef, useState } from 'react';


import { Icon, Tile, cx } from '../../components/ui.tsx';
import { qs } from '../../lib/api.ts';
import { useRates } from '../../lib/data.ts';
import { dateLong, money, rangeLabel, rub, weekday } from '../../lib/format.ts';
import { walletIcon } from '../../lib/icons.tsx';

import { useQuery } from '../../lib/query.ts';
import { Link } from '../../lib/router.tsx';

export function FreeHero({ period, dash, onEditBudget }: { period: BudgetPeriod; dash?: Dashboard; onEditBudget: () => void }) {
  const s = period.summary;
  const today = todayIso();
  const rates = useRates();
  const tx = useQuery<{ items: Transaction[] }>(`/transactions${qs({ from: period.startDate, to: period.endDate, limit: 1000 })}`);
  const days = useMemo(() => {
    const out: string[] = [];
    for (let d = period.startDate; d <= period.endDate; d = addDays(d, 1)) out.push(d);
    return out;
  }, [period.startDate, period.endDate]);
  const perDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of tx.data?.items ?? []) {
      if (t.categoryKind !== 'expense' || t.excludeFromReport) continue;
      m.set(t.date, (m.get(t.date) ?? 0) + (rates.toBase(-t.amount, t.currency) ?? 0));
    }
    return m;
  }, [tx.data, rates.map]);
  const maxDay = Math.max(1, ...days.map((d) => perDay.get(d) ?? 0));
  const dayIndex = days.indexOf(today);
  const current = dayIndex >= 0;
  const over = s.remainder < 0;
  // incomes marked for the period come on top of the budget (absent in a cached answer of an older version)
  const added = s.addedIncome ?? 0;
  const total = Math.max(s.budget + added, s.spent + s.reserved, 1);
  const w = (v: number) => `${Math.max(0, (v / total) * 100)}%`;
  const primary = dash?.primaryWallet;

  const label = current ? `Свободно до ${dateLong(period.endDate)}` : today < period.startDate ? 'Будет свободно в периоде' : 'Осталось в конце периода';
  return (
    <section className="hero" aria-label="Свободные деньги периода и баланс основного счёта">
      {/* two figures checked every day: what the plan leaves free and what is actually on the main account */}
      <div className="hero-figures">
        {/* the main account first, as Money Lover opens on the balance; the period's free money under it */}
        {primary ? (
          <Link to={`/transactions?wallet=${primary.id}`} className="hero-account" aria-label={`${primary.name}: ${money(primary.balance, primary.currency)}, операции счёта`}>
            <span className="hero-label hero-balance-label">
              <Tile icon={walletIcon(primary)} color={primary.color} size="sm" />
              <span className="hero-balance-name">{primary.name}</span>
              <Icon name="right" size={14} />
            </span>
            <RollingMoney className={cx('hero-big', primary.balance < 0 && 'neg')} value={primary.balance} currency={primary.currency} />
          </Link>
        ) : (
          <Link to="/wallets" className="hero-account hero-balance-empty">
            <span className="hero-label">Основной счёт не выбран</span>
            <span className="hero-sub">Выбрать в «Счетах» <Icon name="right" size={14} /></span>
          </Link>
        )}
        <div className="hero-period">
          <span className="hero-label">{over ? 'Перерасход' : label}</span>
          <RollingMoney className={cx('hero-mid', over ? 'neg' : 'pos')} value={over ? -s.remainder : s.remainder} />
          {/* the current period's dates are in its label; per-day pace and forecast are not shown (owner, 2026-10-30) */}
          {!current ? <span className="hero-sub">{rangeLabel(period.startDate, period.endDate)}</span> : null}
        </div>
      </div>

      {/* a day with its bar opens that day's operations */}
      <nav className="ruler" aria-label="Расходы по дням периода">
        {days.map((d) => {
          const v = perDay.get(d) ?? 0;
          const future = d > today;
          const inner = (
            <>
              <span className="ruler-bar"><i style={{ height: future ? undefined : `${Math.max(v > 0 ? 8 : 0, (v / maxDay) * 100)}%` }} /></span>
              <span className="ruler-num">{parseIso(d).getUTCDate()}</span>
              <span className="ruler-wd">{weekday(d, true)}</span>
            </>
          );
          const cls = cx('ruler-day', d === today && 'today', future && 'future');
          return future ? <span key={d} className={cls} aria-hidden>{inner}</span> : (
            <Link key={d} to={`/transactions${qs({ from: d, to: d })}`} className={cls} aria-label={`${dateLong(d)}: ${rub(v)}, операции дня`}>{inner}</Link>
          );
        })}
      </nav>

      <div className="split">
        <div className="split-bar" aria-hidden>
          <span className="split-seg seg-spent" style={{ width: w(s.spent) }} />
          <span className="split-seg seg-reserved" style={{ width: w(s.reserved) }} />
          {!over ? <span className="split-seg seg-free" style={{ width: w(s.remainder) }} /> : null}
        </div>
        <dl className="split-legend">
          <div><dt><i className="dot dot-spent" />Потрачено</dt><dd className="money">{rub(s.spent)}</dd></div>
          <div><dt><i className="dot dot-reserved" />Ждёт оплаты</dt><dd className="money">{rub(s.reserved)}</dd></div>
          <div>
            <dt><i className="dot dot-budget" />Бюджет</dt>
            <dd><button type="button" className="link-like money budget-edit" onClick={onEditBudget} aria-label={`Бюджет ${rub(s.budget)}, изменить`}>{rub(s.budget)}<Icon name="edit" size={14} /></button></dd>
            {added > 0 ? <dd className="split-added money pos" aria-label={`Плюс доходы периода ${rub(added)}`}>+{rub(added)} доходы</dd> : null}
          </div>
        </dl>
      </div>
    </section>
  );
}

export function RollingMoney({ value, currency = 'RUB', className }: { value: number; currency?: string; className?: string }) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = from.current;
    if (start === value || matchMedia('(prefers-reduced-motion: reduce)').matches) { from.current = value; setShown(value); return; }
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / 420);
      const eased = 1 - (1 - k) ** 3;
      const v = start + (value - start) * eased;
      from.current = v;
      setShown(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className={cx('money', className)} aria-label={money(value, currency)}>{money(shown === value ? value : Math.round(shown), currency)}</span>;
}
